import { FormEvent, useState } from 'react';
import { MonoCTA } from './MonoCTA';
import analytics from '@/services/analytics';

// Same env plumbing as the share-note callers (shareLink.ts, SharedNoteView.tsx):
// landing visitors are signed out, so we call the edge function with the anon key.
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_PROJECT_A_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PROJECT_A_ANON_KEY;

type Status = 'idle' | 'submitting' | 'success' | 'error';

/**
 * Release-notification email capture for new platforms and features.
 * `platform: 'updates'` distinguishes these generic signups from the earlier
 * android-specific waitlist rows in the same table.
 */
export function Waitlist({ className, label }: { className?: string; label?: string }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/waitlist-signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ email, platform: 'updates' }),
      });
      if (!res.ok) throw new Error(`waitlist-signup returned ${res.status}`);
      analytics.track('waitlist.signup_submitted', { platform: 'updates' });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }

  return (
    <div className={`font-mono text-sm leading-relaxed ${className ?? ''}`}>
      {status !== 'success' && (
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <span className="text-foreground">
            {label ?? 'want to know when notetime lands on new platforms and features?'}
          </span>
          <div className="flex items-center gap-3">
            <label htmlFor="waitlist-email" className="sr-only">
              email address
            </label>
            <input
              id="waitlist-email"
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              className="min-w-0 flex-1 border-b border-border bg-transparent py-1 font-mono text-sm text-foreground placeholder:text-muted-foreground/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            />
            <MonoCTA size="sm" type="submit" disabled={status === 'submitting'}>
              {status === 'submitting' ? 'saving...' : 'notify me'}
            </MonoCTA>
          </div>
        </form>
      )}
      <p aria-live="polite" className={status === 'error' ? 'mt-2 text-destructive' : 'text-muted-foreground'}>
        {status === 'success'
          ? 'you’re on the list.'
          : status === 'error'
            ? 'something went wrong. try again?'
            : ''}
      </p>
    </div>
  );
}
